import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

function isIpOrLocalhost(host: string): boolean {
  return host === 'localhost' || /^\d+(\.\d+){3}$/.test(host) || /^\[?[0-9a-f:]+\]?$/i.test(host);
}

function deriveBrowserApiHost(): string {
  // Browser only — derive API host from current hostname.
  // - localhost / LAN IP → http://<host>:<port> (dev)
  // - real domain (e.g. crm-testing.gr8.ro) → https://api-<host> (prod/testing)
  const { protocol, hostname } = window.location;
  if (isIpOrLocalhost(hostname)) {
    const port = process.env.NEXT_PUBLIC_API_PORT || '3003';
    return `http://${hostname}:${port}`;
  }
  return `${protocol}//api-${hostname}`;
}

export function getApiBaseUrl(): string {
  // 1. Explicit env var (baked at build time, useful for single-env deploys)
  if (process.env.NEXT_PUBLIC_API_URL) return process.env.NEXT_PUBLIC_API_URL;
  // 2. Browser: derive from current hostname so both LAN IPs and prod/testing domains work
  if (typeof window !== 'undefined') return `${deriveBrowserApiHost()}/api/v2`;
  // 3. SSR fallback
  const port = process.env.NEXT_PUBLIC_API_PORT || '3003';
  return `http://localhost:${port}/api/v2`;
}

/** WebSocket base URL (API host without /api/v2 path) */
export function getWsBaseUrl(): string {
  if (process.env.NEXT_PUBLIC_API_URL)
    return process.env.NEXT_PUBLIC_API_URL.replace('/api/v2', '');
  if (typeof window !== 'undefined') return deriveBrowserApiHost();
  const port = process.env.NEXT_PUBLIC_API_PORT || '3003';
  return `http://localhost:${port}`;
}

export const apiClient = axios.create({
  baseURL: getApiBaseUrl(),
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000, // 30 seconds
  withCredentials: true, // sends HttpOnly refresh cookie on cross-origin requests
});

// SECURITY: accessToken is kept in a module-level variable (memory) instead of localStorage
// to protect against XSS attacks that can read localStorage.
// refreshToken is stored in an HttpOnly cookie (set by the API) — inaccessible to JavaScript.
let _accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  _accessToken = token;
}

export function getAccessToken(): string | null {
  return _accessToken;
}

apiClient.interceptors.request.use((config) => {
  const token = _accessToken;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (typeof window !== 'undefined') {
    const companyId = localStorage.getItem('activeCompanyId');
    if (companyId) config.headers['X-Company-Id'] = companyId;
    const locationId = localStorage.getItem('activeLocationId');
    if (locationId) config.headers['X-Location-Id'] = locationId;
  }
  return config;
});

// Strip null/undefined/empty-string values from POST/PATCH/PUT request bodies
// to prevent DTO validation errors from empty form fields being sent to the API
apiClient.interceptors.request.use((config) => {
  if (
    config.data &&
    typeof config.data === 'object' &&
    ['post', 'patch', 'put'].includes(config.method || '')
  ) {
    config.data = Object.fromEntries(
      Object.entries(config.data).filter(([_, v]) => {
        if (v === null || v === undefined || v === '') return false;
        // Strip empty plain objects {} to avoid DTO validation errors (e.g. customFields: {})
        if (typeof v === 'object' && !Array.isArray(v) && Object.keys(v as object).length === 0)
          return false;
        return true;
      }),
    );
  }
  return config;
});

// Refresh token lock — prevents multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];
let refreshRejectSubscribers: Array<(err: unknown) => void> = [];

function onTokenRefreshed(newToken: string) {
  refreshSubscribers.forEach((cb) => cb(newToken));
  refreshSubscribers = [];
  refreshRejectSubscribers = [];
}

function onRefreshFailed(err: unknown) {
  refreshRejectSubscribers.forEach((cb) => cb(err));
  refreshSubscribers = [];
  refreshRejectSubscribers = [];
}

function addRefreshSubscriber(resolve: (token: string) => void, reject: (err: unknown) => void) {
  refreshSubscribers.push(resolve);
  refreshRejectSubscribers.push(reject);
}

/**
 * Called by AuthProvider at the start of its initial silent token refresh.
 * Marks the refresh lock so that any 401s fired before auth completes are
 * queued rather than immediately triggering forceLogout().
 */
export function beginInitialRefresh(): void {
  isRefreshing = true;
}

/**
 * Called by AuthProvider when the initial refresh resolves (success or failure).
 * Flushes queued subscribers with the new token, or rejects them on failure.
 */
export function resolveInitialRefresh(newToken: string | null, err?: unknown): void {
  isRefreshing = false;
  if (newToken) {
    onTokenRefreshed(newToken);
  } else {
    onRefreshFailed(err ?? new Error('Initial refresh failed'));
  }
}

function forceLogout() {
  // Clear in-memory access token
  _accessToken = null;
  // Clear all auth-related items from localStorage on logout
  if (typeof window !== 'undefined') {
    localStorage.removeItem('accessToken'); // legacy key — may still exist from previous sessions
    localStorage.removeItem('refreshToken'); // cleanup any leftover migration token
    localStorage.removeItem('user');
    if (!window.location.pathname.startsWith('/login')) {
      window.location.href = '/login';
    }
  }
}

// Error logging interceptor — report 4xx/5xx API errors to /error-logs
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const status = error.response?.status;
    const url = error.config?.url || '';
    // Skip reporting errors for URLs containing /undefined (data still loading)
    if (url.includes('/undefined') || url.includes('undefined')) return Promise.reject(error);
    // Report 4xx/5xx errors (skip auth endpoints, skip error-logs itself, skip 429 to avoid
    // a cascade where rate-limit errors trigger more error-log POSTs which hit the throttler)
    if (
      status &&
      status >= 400 &&
      status !== 401 &&
      status !== 403 &&
      status !== 429 &&
      !url.includes('error-logs')
    ) {
      const message = `API ${status}: ${error.config?.method?.toUpperCase()} ${url}`;
      const detail = (error.response?.data as Record<string, unknown>)?.message || '';
      // Fire-and-forget — use raw axios to avoid infinite loop
      apiClient
        .post('/error-logs', {
          message: detail ? `${message} — ${detail}` : message,
          url: typeof window !== 'undefined' ? window.location.href : undefined,
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
          severity: status >= 500 ? 'error' : 'warning',
          component: `API ${error.config?.method?.toUpperCase()} ${url}`,
          metadata: { status, apiUrl: url, method: error.config?.method },
        })
        .catch(() => {
          // error-logs endpoint unavailable — silently ignore
        });
    }
    return Promise.reject(error);
  },
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    if (!originalRequest || error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    // If already refreshing, queue this request to retry after refresh completes
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        addRefreshSubscriber(
          (newToken: string) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(apiClient(originalRequest));
          },
          (err: unknown) => {
            reject(err);
          },
        );
      });
    }

    isRefreshing = true;

    try {
      const res = await axios.post(
        `${getApiBaseUrl()}/auth/refresh`,
        {}, // empty body — HttpOnly cookie is sent automatically by the browser
        { withCredentials: true },
      );

      const newAccessToken = res.data.accessToken;
      // Store new access token in memory only (not localStorage) — XSS protection
      setAccessToken(newAccessToken);

      isRefreshing = false;
      onTokenRefreshed(newAccessToken);

      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      return apiClient(originalRequest);
    } catch (refreshErr) {
      isRefreshing = false;
      onRefreshFailed(refreshErr);
      forceLogout();
      return Promise.reject(error);
    }
  },
);
